import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";

/**
 * 리뷰 큐에서 하는 mutation 의 경계다.
 *
 * 큐는 별도 엔드포인트가 아니다. Dataset 목록의 `review_queue=true` 조회다(`listDatasets`).
 * 그래서 두 목록은 같은 facet 과 응답을 쓴다. 이 모듈은 설정(embodiment·tip·label)과 직교하는
 * 축을 다룬다:
 * - 확정 표시
 * - video fps 지정
 * - 수집 방식 지정
 * 생성자와 admin 만 이 요청을 쓸 수 있다. 그 외 요청자는 403 `dataset.write_forbidden` 을 받는다.
 */

// 설정을 바꾸지 않고 검토 완료만 표시한다(body 없음, 204). 설정 확정도 같은 축(`reviewed=true`)을
// 설정한다. 한 방향으로만 바뀌므로 해제 라우트는 없다. 이미 true 면 no-op 이다.
export async function markDatasetReviewed(datasetUuid: string): Promise<void> {
  await apiClient.post(apiRoutes.dataset.markReviewed(datasetUuid));
}

// fps 지정 결과다. 서버가 무엇을 채웠고 요약이 어떻게 바뀌었는지 알려 준다.
// `filled_file_count=0` 은 채울 대상이 없었다는 뜻이고, 실패가 아니다(이미 값이 있었거나
// video 파일이 없다).
export type DatasetVideoFpsResult = {
  fps: number;
  video_file_count: number;
  filled_file_count: number;
  primary_rate_hz: number | null;
};

/**
 * import 가 채우지 못한 video fps 를 리뷰어가 지정한다.
 *
 * **서버는 비어 있는 행만 채운다.** 이미 값이 있는 video 파일은 서버가 바꾸지 않는다.
 * 그래서 이 호출은 probe 로 얻은 fps 를 사람 입력으로 덮어쓰지 않는다. 잘못 기록된 값은
 * 원본 mp4 를 다시 측정하는 backfill(운영 CLI)이 고친다.
 *
 * 서버는 `fps > 0` 인 유한값만 받는다(아니면 422). 지정한 값은 `Dataset.inspect_result` 가
 * 아니라 video `EpisodeFile.type_details.fps` 에 저장된다. 요약 `primary_rate_hz` 는 그 값들의
 * median 으로 갱신된다.
 */
export async function designateDatasetVideoFps(
  datasetUuid: string,
  fps: number,
): Promise<DatasetVideoFpsResult> {
  const response = await apiClient.put<DatasetVideoFpsResult>(
    apiRoutes.dataset.reviewVideoFps(datasetUuid),
    { fps },
  );
  return response.data;
}
